import {ActionTree, Commit} from 'vuex';
import {applyPatch, Operation} from 'fast-json-patch';
import {cloneDeep} from 'lodash';
import {AggregationEnum, FileDownloadModel} from '@/libs/Api';
import {Guid} from '@/libs/common/functions/guid';
import {CrudAction, CrudResponseAction} from '@/libs/core/crud/crud-action';
import {createCrudQueryPayload, CrudDataQuery} from '@/libs/core/crud/crud-query';
import {CrudEntity, CrudService} from '@/libs/core/crud/crud-service';
import {CrudStoreState, LatestRequestIds} from '@/libs/core/crud/crud-store-state';
import {
    CreatePayload, DeletePayload, DownloadPayload, GetAllPayload, GetGroupedPayload, GetPayload, GetSumPayload,
    PartialUpdatePayload, UpdatePayload
} from '@/libs/core/crud/crud-store-payloads';

export function createCrudActions<T extends CrudEntity, TDetail extends CrudEntity>(crudService: CrudService<T, TDetail>): ActionTree<CrudStoreState<T, TDetail>, unknown> {
    type State = CrudStoreState<T, TDetail>;

    const startRequest = (commit: Commit, operation: keyof LatestRequestIds, key: string) => {
        const requestId = Guid.NewGuid();
        commit(CrudAction.SetLatestRequestId, {operation, key, requestId});
        return requestId;
    };

    const isLatest = (state: State, operation: keyof LatestRequestIds, key: string, requestId: string) =>
        requestId === state.latestRequestIds[operation][key];

    const echo = (requestId: string) => ({headers: {EchoId: requestId}});

    const fail = (commit: Commit, mutation: CrudResponseAction, key: string, errorMessage: string, error: unknown) => {
        console.error(error);
        commit(mutation, {key, errorMessage});
    };

    return {
        [CrudAction.GetAll]: async ({commit, state}, payload: GetAllPayload<T>) => {
            const storeKey = payload.key || '';
            try {
                commit(CrudAction.GetAll, payload);

                for (const query of resolveQueries(state, payload, storeKey)) {
                    if (query.useCache && JSON.stringify(query) === JSON.stringify(state.dataQuery[storeKey])) {
                        commit(CrudResponseAction.GetAllNoop, {key: storeKey});
                        continue;
                    }

                    const requestId = startRequest(commit, 'data', storeKey);
                    query.params = {
                        ...query.params,
                        ...echo(requestId)
                    };
                    const result = await crudService.getAll(query);
                    if (isLatest(state, 'data', storeKey, requestId)) {
                        const page = (<any>result).data.returnValue;
                        commit(CrudResponseAction.GetAllSuccess, {
                            data: page.items,
                            currentPage: page.currentPage,
                            rowCount: page.rowCount,
                            pageSize: page.pageSize,
                            pageCount: page.pageCount,
                            query,
                            key: storeKey
                        });
                    }
                }
            } catch (ex) {
                fail(commit, CrudResponseAction.GetAllFailed, storeKey, 'Fetch data request has failed.', ex);
            }
        },

        [CrudAction.Get]: async ({commit, state}, payload: GetPayload) => {
            const storeKey = payload.key || '';
            try {
                commit(CrudAction.Get, payload);
                const requestId = startRequest(commit, 'detail', storeKey);
                const result = await crudService.get(payload.id!, echo(requestId));
                if (isLatest(state, 'detail', storeKey, requestId)) {
                    commit(CrudResponseAction.GetSuccess, {key: storeKey, item: result.data.returnValue});
                }
            } catch (ex) {
                fail(commit, CrudResponseAction.GetFailed, storeKey, 'Fetch item request has failed.', ex);
            }
        },

        [CrudAction.Create]: async ({commit, state, dispatch}, payload: CreatePayload<TDetail>) => {
            const storeKey = payload.key || '';
            try {
                commit(CrudAction.Create, payload);
                const result = await crudService.create(payload.item);
                commit(CrudResponseAction.CreateSuccess, {key: storeKey, item: result.data.returnValue});
                if (state.dataQuery[storeKey]) {
                    dispatch(CrudAction.GetAll, {key: storeKey, query: state.dataQuery[storeKey]});
                }
            } catch (ex) {
                fail(commit, CrudResponseAction.CreateFailed, storeKey, 'Create item has failed.', ex);
            }
        },

        [CrudAction.Update]: async ({commit}, payload: UpdatePayload<TDetail>) => {
            const storeKey = payload.key || '';
            try {
                commit(CrudAction.Update, payload);
                const updatedItem = await crudService.update(payload.item);
                commit(CrudResponseAction.UpdateSuccess, {key: storeKey, item: updatedItem});
            } catch (ex) {
                fail(commit, CrudResponseAction.UpdateFailed, storeKey, 'Update item has failed.', ex);
            }
        },

        [CrudAction.PartialUpdate]: async ({commit, state}, payload: PartialUpdatePayload) => {
            const storeKey = payload.key || '';
            try {
                commit(CrudAction.PartialUpdate, payload);

                let updatedItem: TDetail | undefined;
                if (!payload.extra?.local) {
                    updatedItem = await crudService.partialUpdate(payload.id, payload.diff);
                } else if (state.detail[storeKey]) {
                    updatedItem = applyPatch<TDetail>(cloneDeep(state.detail[storeKey]) as TDetail, payload.diff).newDocument;
                }

                commit(CrudResponseAction.PartialUpdateSuccess, {
                    key: storeKey,
                    id: payload.id,
                    item: updatedItem,
                    data: applyPatchToItems(state.data, payload.id, payload.diff),
                    extra: payload.extra
                });
            } catch (ex) {
                fail(commit, CrudResponseAction.PartialUpdateFailed, storeKey, 'Partial Update item has failed.', ex);
            }
        },

        [CrudAction.Delete]: async ({commit, state}, payload: DeletePayload<T>) => {
            const storeKey = payload.key || '';
            try {
                commit(CrudAction.Delete, {key: payload.key, id: payload.id});
                const query = payload.query || createCrudQueryPayload<T>(undefined, [
                    {field: 'id', op: 'eq', comparand: payload.id!}
                ]).query;

                let deletedItems: T[];
                if (!payload.extra?.local) {
                    deletedItems = (<any>await crudService.delete(query)).items;
                } else {
                    const id = payload.id || query.data.predicates.find(p => p.field === 'id')?.comparand;
                    deletedItems = state.data[storeKey].filter(item => item.id === id);
                }

                commit(CrudResponseAction.DeleteSuccess, {data: deletedItems, key: storeKey});
            } catch (ex) {
                fail(commit, CrudResponseAction.DeleteFailed, storeKey, 'Delete item has failed.', ex);
            }
        },

        [CrudAction.Download]: async ({commit}, payload: DownloadPayload<T>) => {
            const storeKey = payload.key || '';
            try {
                const result = await crudService.download(payload.query);
                const file: FileDownloadModel = (<any>result).data.returnValue;
                saveFile(file);
                commit(CrudResponseAction.DownloadSuccess, {data: file, query: payload.query, key: storeKey});
            } catch (ex) {
                fail(commit, CrudResponseAction.DownloadFailed, storeKey, 'Download data request has failed.', ex);
            }
        },

        [CrudAction.GetSum]: async ({commit, state}, payload: GetSumPayload) => {
            const storeKey = payload.key || '';
            try {
                payload.query.aggregation = AggregationEnum.Sum;
                commit(CrudAction.GetSum, payload);

                const requestId = startRequest(commit, 'sum', storeKey);
                const sum = await crudService.getAggregation(payload.query, echo(requestId));
                if (isLatest(state, 'sum', storeKey, requestId)) {
                    commit(CrudResponseAction.GetSumSuccess, {key: storeKey, item: sum});
                }
            } catch (ex) {
                fail(commit, CrudResponseAction.GetSumFailed, storeKey, 'Fetch sum request has failed.', ex);
            }
        },

        [CrudAction.GetGrouped]: async ({commit, state}, payload: GetGroupedPayload) => {
            const storeKey = payload.key || '';
            try {
                commit(CrudAction.GetGrouped, payload);

                const requestId = startRequest(commit, 'groupedData', storeKey);
                const result = await crudService.getGrouped(payload.query, echo(requestId));
                if (isLatest(state, 'groupedData', storeKey, requestId)) {
                    commit(CrudResponseAction.GetGroupedSuccess, {
                        data: result.data.returnValue,
                        query: payload.query,
                        key: storeKey
                    });
                }
            } catch (ex) {
                fail(commit, CrudResponseAction.GetGroupedFailed, storeKey, 'Fetch grouped data request has failed.', ex);
            }
        },
    };
}

function resolveQueries<T>(state: { dataQuery: Dictionary<CrudDataQuery<T>> }, payload: GetAllPayload<T>, storeKey: string): CrudDataQuery<T>[] {
    if (payload.query !== 'same')
        return [payload.query];

    return Object.keys(state.dataQuery)
        .filter(key => (payload.extra?.ignoreKeySuffix ? key.split('_')[0] : key) === storeKey)
        .map(key => state.dataQuery[key]);
}

function applyPatchToItems<T extends CrudEntity>(data: Dictionary<T[]>, itemId: string, operations: Operation[]): Dictionary<T[]> {
    const updatedData: Dictionary<T[]> = {};
    operations.forEach(x => {
        if (!x.path.startsWith("/"))
            x.path = "/".concat(x.path);
    });
    Object.keys(data).forEach(key => {
        const itemIndex = data[key].findIndex(item => item.id === itemId);
        if (itemIndex > -1) {
            updatedData[key] = data[key].map((item, index) => index === itemIndex
                ? applyPatch<T>(cloneDeep(item) as T, operations).newDocument
                : item);
        }
    });

    return updatedData;
}

function saveFile(file: FileDownloadModel) {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(b64toBlob(file.base64!, file.contentType!));
    link.setAttribute('download', file.filename!);
    document.body.appendChild(link);
    link.click();
    link.parentNode!.removeChild(link);
}

function b64toBlob(b64Data: string, contentType = '', sliceSize = 512): Blob {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, {type: contentType});
}
